// src/modules/appAccesos/AccesosAppTab.jsx
//
// /admin > "Accesos app": que usuarios ven cada Caja en la app Android (TRS Sort). Un switch
// por usuario y caja; cada cambio se guarda al instante. Solo decide lo que se MUESTRA en la
// app: lo que puede hacer dentro de la caja sigue el criterio de rol del backend.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { listAccesosApp, setAccesosAppUsuario } from "../../services/appAccesosService";

const sinTildes = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export default function AccesosAppTab({ notify }) {
  const [modulos, setModulos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [buscar, setBuscar] = useState("");
  const [soloConAcceso, setSoloConAcceso] = useState(false);
  // ids de usuario con un guardado en curso: se bloquea la fila entera para que dos PUT del mismo usuario no se pisen
  const [guardando, setGuardando] = useState(() => new Set());

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await listAccesosApp();
      setModulos(data?.data?.modulos || []);
      setUsuarios(data?.data?.usuarios || []);
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudieron cargar los accesos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const visibles = useMemo(() => {
    const q = sinTildes(buscar.trim());
    return usuarios.filter((u) => {
      if (soloConAcceso && u.modulos.length === 0) return false;
      if (!q) return true;
      return [u.nombre, u.username, u.role, u.sub_rol, u.sucursal].some((v) => sinTildes(v).includes(q));
    });
  }, [usuarios, buscar, soloConAcceso]);

  const conteoPorModulo = useMemo(() => {
    const conteo = {};
    for (const u of usuarios) for (const m of u.modulos) conteo[m] = (conteo[m] || 0) + 1;
    return conteo;
  }, [usuarios]);

  const toggle = async (usuario, clave) => {
    const anteriores = usuario.modulos;
    const nuevos = anteriores.includes(clave) ? anteriores.filter((m) => m !== clave) : [...anteriores, clave];

    // Optimista: se marca ya y se revierte si el guardado falla.
    setUsuarios((prev) => prev.map((u) => (u.id === usuario.id ? { ...u, modulos: nuevos } : u)));
    setGuardando((prev) => new Set(prev).add(usuario.id));
    try {
      await setAccesosAppUsuario(usuario.id, nuevos);
    } catch (e) {
      setUsuarios((prev) => prev.map((u) => (u.id === usuario.id ? { ...u, modulos: anteriores } : u)));
      notify(e?.response?.data?.message || "No se pudo guardar el acceso.", "error");
    } finally {
      setGuardando((prev) => {
        const s = new Set(prev);
        s.delete(usuario.id);
        return s;
      });
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Accesos a la app móvil
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Marca qué cajas ve cada usuario en la app TRS Sort. Los cambios se guardan al instante y el usuario los ve
        la próxima vez que abre el menú de la app.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre, usuario, rol o sucursal..."
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
          sx={{ minWidth: 320 }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={soloConAcceso} onChange={(e) => setSoloConAcceso(e.target.checked)} />}
          label="Solo usuarios con algún acceso"
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: "65vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sucursal</TableCell>
                {modulos.map((m) => (
                  <TableCell key={m.clave} align="center" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {m.nombre}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {conteoPorModulo[m.clave] || 0} usuario(s)
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3 + modulos.length}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 2 }}>
                      No se encontraron usuarios.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibles.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.username}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={u.sub_rol ? `${u.role} · ${u.sub_rol}` : u.role} />
                    </TableCell>
                    <TableCell>{u.sucursal}</TableCell>
                    {modulos.map((m) => {
                      return (
                        <TableCell key={m.clave} align="center">
                          <Tooltip title={`${m.nombre} para ${u.nombre}`}>
                            <span>
                              <Switch
                                size="small"
                                checked={u.modulos.includes(m.clave)}
                                disabled={guardando.has(u.id)}
                                onChange={() => toggle(u, m.clave)}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
